// ALT CVLT filters — explicit parent choices and one shared pending state.
// Replaces the previous facet-apply.js; do not load both versions.
(function () {
  const DESKTOP = 750;
  const LOCKED_FILTER = 'filter.p.m.custom.show_in_collections';
  const GROUP_PARAM = 'alt_group';
  const GROUP_VALUES = new Map();
  const GROUP_LABELS = new Map();
  const GROUP_ANCESTORS = new Map();
  const pairKey = (name, value) => JSON.stringify([name, value]);
  const isDesktop = () => window.innerWidth >= DESKTOP;
  const forms = () => [...document.querySelectorAll('facets-form-component')];
  const mainForm = () => forms().find(f => !f.closest('dialog') && f.offsetParent !== null) || forms()[0];
  let draftParams = new URLSearchParams(location.search);
  let draftPath = location.pathname;
  let pendingGroups = new Set(draftParams.getAll(GROUP_PARAM));
  let appliedSignature = '';
  let scheduled = false;
  const LOCKED_VALUE = draftParams.get(LOCKED_FILTER) ||
    document.querySelector(`input[name="${LOCKED_FILTER}"]`)?.value || 'true';

  function uniqueParams(params) {
    const result = new URLSearchParams();
    for (const [name, value] of params) {
      if (value !== '' && !result.getAll(name).includes(value)) result.append(name, value);
    }
    return result;
  }

  function cacheAllGroups() {
    document.querySelectorAll('.facets__parent-input[data-nav-collection]').forEach(parent => {
      const handle = parent.dataset.navCollection;
      const container = parent.closest('details');
      if (!handle || !container) return;
      const label = parent.closest('summary')?.querySelector('.facets__group-label, .facets__subgroup-label');
      if (label) GROUP_LABELS.set(handle, label.textContent.trim());
      // Never let a filtered section shrink the definition cached on this page.
      const pairs = new Map((GROUP_VALUES.get(handle) || []).map(pair => [pairKey(...pair), pair]));
      container.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach(input => {
        if (input.name !== LOCKED_FILTER && input.value) pairs.set(pairKey(input.name, input.value), [input.name, input.value]);
      });
      GROUP_VALUES.set(handle, [...pairs.values()]);
      const ancestors = [];
      let node = container.parentElement?.closest('details');
      while (node) {
        const ancestor = node.querySelector(':scope > summary .facets__parent-input[data-nav-collection]');
        if (ancestor) ancestors.push(ancestor.dataset.navCollection);
        node = node.parentElement?.closest('details');
      }
      GROUP_ANCESTORS.set(handle, ancestors);
    });
  }

  function activeGroupValues(handle, params) {
    const values = GROUP_VALUES.get(handle) || [];
    return values.length && values.every(([name, value]) => params.getAll(name).includes(value)) ? values : [];
  }

  function writeGroupChoices(params, choices = pendingGroups) {
    params.delete(GROUP_PARAM);
    for (const handle of choices) {
      if (activeGroupValues(handle, params).length) params.append(GROUP_PARAM, handle);
    }
    return params;
  }

  function signature(params, path, groups) {
    const clean = writeGroupChoices(uniqueParams(params), groups);
    const pairs = [...clean].filter(([key]) => (key.startsWith('filter.') && key !== LOCKED_FILTER) || key === GROUP_PARAM);
    return JSON.stringify([path, pairs.map(pair => pairKey(...pair)).sort()]);
  }

  function readApplied(url = new URL(location.href)) {
    draftParams = uniqueParams(url.searchParams);
    draftPath = url.pathname;
    pendingGroups = new Set(draftParams.getAll(GROUP_PARAM).filter(handle => activeGroupValues(handle, draftParams).length));
    appliedSignature = signature(draftParams, draftPath, pendingGroups);
  }

  function setBox(input, checked) {
    if (input.checked !== checked) input.checked = checked;
    input.indeterminate = false;
    if (input.hasAttribute('checked') !== checked) input.toggleAttribute('checked', checked);
  }

  function syncCheckboxes() {
    document.querySelectorAll('facets-form-component input[type="checkbox"], facets-form-component input[type="radio"]').forEach(input => {
      if (input.classList.contains('facets__parent-input')) {
        // Shared child product types must never select another parent.
        setBox(input, pendingGroups.has(input.dataset.navCollection));
      } else if (input.name.startsWith('filter.') && input.name !== LOCKED_FILTER) {
        setBox(input, draftParams.getAll(input.name).includes(input.value));
      } else {
        const item = input.closest('.facets__inputs-list-item--exclusive');
        if (item) setBox(input, draftPath === '/collections/' + item.dataset.exclusiveCollection &&
          draftParams.getAll(item.dataset.exclusiveFilter).includes(item.dataset.exclusiveValue));
      }
    });
  }

  function chooseParent(handle, checked) {
    const values = GROUP_VALUES.get(handle) || [];
    if (!values.length) return;
    const scope = new Set(values.map(pair => pairKey(...pair)));
    const owned = new Set([...pendingGroups].flatMap(group => GROUP_VALUES.get(group) || []).map(pair => pairKey(...pair)));
    // Keep direct selections outside this parent's scope, and preserve the
    // ownership of shared values by other explicitly selected groups.
    const direct = [...draftParams].filter(([name, value]) =>
      name.startsWith('filter.') && name !== LOCKED_FILTER && !owned.has(pairKey(name, value)) && !scope.has(pairKey(name, value)));
    for (const group of pendingGroups) {
      if (group === handle || GROUP_ANCESTORS.get(group)?.includes(handle) || GROUP_ANCESTORS.get(handle)?.includes(group)) {
        pendingGroups.delete(group);
      }
    }
    if (checked) pendingGroups.add(handle);
    for (const name of [...draftParams.keys()]) if (name.startsWith('filter.') && name !== LOCKED_FILTER) draftParams.delete(name);
    for (const [name, value] of [...direct, ...[...pendingGroups].flatMap(group => GROUP_VALUES.get(group) || [])]) {
      if (!draftParams.getAll(name).includes(value)) draftParams.append(name, value);
    }
  }

  function chooseLeaf(input) {
    // Altering one child makes that group a custom set of individual values.
    for (const group of pendingGroups) {
      if ((GROUP_VALUES.get(group) || []).some(([name, value]) => name === input.name && value === input.value)) pendingGroups.delete(group);
    }
    if (input.type === 'radio') draftParams.delete(input.name);
    else draftParams.delete(input.name, input.value);
    if (input.checked) draftParams.append(input.name, input.value);
  }

  function chooseExclusive(input, item) {
    const path = '/collections/' + item.dataset.exclusiveCollection;
    if (input.checked && draftPath !== path) {
      draftParams = new URLSearchParams([[LOCKED_FILTER, LOCKED_VALUE]]);
      pendingGroups.clear();
      draftPath = path;
    }
    draftParams.delete(item.dataset.exclusiveFilter, item.dataset.exclusiveValue);
    if (input.checked) draftParams.append(item.dataset.exclusiveFilter, item.dataset.exclusiveValue);
    if (![...draftParams.keys()].some(key => key.startsWith('filter.') && key !== LOCKED_FILTER)) draftPath = '/collections/all';
  }

  function draftURL() {
    const url = new URL(location.href);
    url.pathname = draftPath;
    const params = uniqueParams(draftParams);
    params.set(LOCKED_FILTER, LOCKED_VALUE);
    params.delete('page');
    writeGroupChoices(params);
    url.search = params.toString();
    return url;
  }

  function commit(form = mainForm()) {
    const url = draftURL();
    // Native Horizon updates history synchronously and renders asynchronously.
    // Pending state is committed independently of the old DOM being replaced.
    if (url.pathname !== location.pathname || typeof form?.updateFiltersByURL !== 'function') {
      location.assign(url.toString());
      return;
    }
    form.updateFiltersByURL(url.pathname + url.search + url.hash);
    refreshUI();
  }

  function clearAll() {
    for (const name of [...draftParams.keys()]) if (name.startsWith('filter.') || name === GROUP_PARAM) draftParams.delete(name);
    draftParams.set(LOCKED_FILTER, LOCKED_VALUE);
    pendingGroups.clear();
    commit();
  }

  function reset() {
    draftPath = '/collections/all';
    clearAll();
  }

  function patchForm(form) {
    if (form.__altCvltFiltersV2 || typeof form.updateFiltersByURL !== 'function' || typeof form.createURLParameters !== 'function') return;
    form.__altCvltFiltersV2 = true;
    const nativeParameters = form.createURLParameters.bind(form);
    const nativeByURL = form.updateFiltersByURL.bind(form);
    form.createURLParameters = function (...args) {
      return args.length ? writeGroupChoices(uniqueParams(nativeParameters(...args))) : draftURL().searchParams;
    };
    form.updateFiltersByURL = function (url, ...args) {
      readApplied(new URL(url, location.href));
      const result = nativeByURL(url, ...args);
      refreshUI();
      return result;
    };
    form.updateFilters = function () {
      // Checkbox events are handled in capture below. Native calls here include
      // price validation, per-filter clearing and sorting.
      const params = uniqueParams(nativeParameters());
      const represented = new Set([...form.querySelectorAll('input[name^="filter."]')].map(input => input.name));
      for (const name of represented) {
        if (name === LOCKED_FILTER) continue;
        draftParams.delete(name);
        for (const value of params.getAll(name)) if (value) draftParams.append(name, value);
      }
      for (const handle of pendingGroups) if (!activeGroupValues(handle, draftParams).length) pendingGroups.delete(handle);
      if (params.has('sort_by')) draftParams.set('sort_by', params.get('sort_by'));
      if (params.has('q')) draftParams.set('q', params.get('q'));
      if (isDesktop()) refreshUI();
      else commit(form);
    };
  }

  function ensureBar() {
    let bar = document.getElementById('facet-apply-bar');
    if (bar) return bar;
    bar = document.createElement('div');
    bar.id = 'facet-apply-bar';
    bar.innerHTML = '<button type="button" id="facet-apply-btn">Apply filters</button><button type="button" id="facet-apply-reset">Reset</button>';
    bar.querySelector('#facet-apply-btn').addEventListener('click', () => commit());
    bar.querySelector('#facet-apply-reset').addEventListener('click', reset);
    document.body.appendChild(bar);
    return bar;
  }

  function refreshBar() {
    const pending = signature(draftParams, draftPath, pendingGroups) !== appliedSignature;
    const show = isDesktop() && pending;
    const bar = show ? ensureBar() : document.getElementById('facet-apply-bar');
    if (bar) bar.classList.toggle('is-visible', show);
    if (show) clampPosition();
  }

  function clampPosition() {
    const bar = document.getElementById('facet-apply-bar');
    if (!bar?.classList.contains('is-visible')) return;
    const anchor = document.querySelector('.main-collection-grid') || document.querySelector('.facets-block-wrapper') || mainForm();
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const minimum = Math.max(rect.top + 16, 16);
    const maximum = Math.max(minimum, rect.bottom - bar.offsetHeight - 16);
    bar.style.top = Math.min(maximum, Math.max(minimum, innerHeight * 0.35)) + 'px';
    bar.style.transform = 'none';
  }

  function updateClearAll() {
    const form = mainForm();
    const wrapper = form?.querySelector('.facets__filters-wrapper');
    if (!wrapper) return;
    let button = document.getElementById('facet-clear-all-btn');
    if (!button) {
      button = document.createElement('button');
      button.id = 'facet-clear-all-btn';
      button.type = 'button';
      button.className = 'button-unstyled';
      button.textContent = 'Clear all filters';
      button.addEventListener('click', clearAll);
    }
    if (button.nextElementSibling !== wrapper) wrapper.before(button);
    const active = [draftParams, new URLSearchParams(location.search)].some(params => [...params.keys()].some(key => key.startsWith('filter.') && key !== LOCKED_FILTER));
    button.style.display = isDesktop() && active ? 'block' : 'none';
  }

  function groupRemovalURL(handle, applied, groups) {
    const removed = new Set((GROUP_VALUES.get(handle) || []).map(([name, value]) => pairKey(name, value)));
    const retained = new Set(groups.filter((other) => other !== handle)
      .flatMap((other) => GROUP_VALUES.get(other) || [])
      .map(([name, value]) => pairKey(name, value)));
    const url = new URL(location.href);
    url.search = '';
    for (const [name, value] of applied) {
      if (name === GROUP_PARAM || name === 'page') continue;
      if (name === LOCKED_FILTER || !removed.has(pairKey(name, value)) || retained.has(pairKey(name, value))) {
        url.searchParams.append(name, value);
      }
    }
    if (!url.searchParams.has(LOCKED_FILTER)) url.searchParams.set(LOCKED_FILTER, LOCKED_VALUE);
    writeGroupChoices(url.searchParams, groups.filter((other) => other !== handle));
    return url.pathname + url.search + url.hash;
  }

  function updateGroupPills() {
    cacheAllGroups();
    const applied = new URLSearchParams(location.search);
    const groups = [...new Set(applied.getAll(GROUP_PARAM))]
      .filter((handle) => GROUP_LABELS.has(handle) && activeGroupValues(handle, applied).length);
    const covered = new Set(groups.flatMap((handle) => GROUP_VALUES.get(handle) || [])
      .map(([name, value]) => pairKey(name, value)));

    document.querySelectorAll('.facets-remove').forEach((list) => {
      const pills = [...list.querySelectorAll('facet-remove-component[data-alt-filter-name]')];
      // Applied chips remain tied to the URL; pending checkbox changes never
      // turn the last applied parent back into a list of child chips.
      const appliedKeys = new Set([...applied].map(([name, value]) => pairKey(name, value)));
      const displayedGroups = groups;
      const visibleGroups = new Set(displayedGroups);

      list.querySelectorAll('[data-alt-group-pill]').forEach((pill) => {
        if (!visibleGroups.has(pill.dataset.altGroupPill)) pill.remove();
      });
      pills.forEach((pill) => {
        const key = pairKey(pill.dataset.altFilterName, pill.dataset.altFilterValue);
        const hide = covered.has(key) || !appliedKeys.has(key);
        pill.toggleAttribute('data-alt-group-hidden', hide);
      });

      // Keep the parent-choice metadata on Horizon's native individual removals.
      list.querySelectorAll('facet-remove-component[data-url]').forEach((pill) => {
        const url = new URL(pill.dataset.url, location.origin);
        writeGroupChoices(url.searchParams, groups);
        if (!url.searchParams.has(LOCKED_FILTER)) url.searchParams.set(LOCKED_FILTER, LOCKED_VALUE);
        const next = url.pathname + url.search + url.hash;
        if (pill.dataset.url !== next) pill.dataset.url = next;
      });

      for (const handle of displayedGroups) {
        let pill = [...list.querySelectorAll('[data-alt-group-pill]')]
          .find((node) => node.dataset.altGroupPill === handle);
        if (!pill) {
          pill = document.createElement('a');
          pill.dataset.altGroupPill = handle;
          pill.className = 'pills__pill pills__pill--desktop-small facets-remove__pill';
          const label = document.createElement('span');
          label.textContent = GROUP_LABELS.get(handle);
          pill.appendChild(label);
          const icon = pills[0]?.querySelector('.svg-wrapper');
          if (icon) pill.appendChild(icon.cloneNode(true));
          else pill.appendChild(document.createTextNode(' ×'));
          pill.setAttribute('aria-label', 'Remove ' + GROUP_LABELS.get(handle) + ' filter');
          list.insertBefore(pill, pills[0] || null);
        }
        const href = groupRemovalURL(handle, applied, groups);
        if (pill.getAttribute('href') !== href) pill.setAttribute('href', href);
      }
    });
  }

  function refreshUI() {
    cacheAllGroups();
    forms().forEach(patchForm);
    syncCheckboxes();
    refreshBar();
    updateClearAll();
    updateGroupPills();
  }

  function scheduleRefresh() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      refreshUI();
    });
  }

  // Horizon delegates changes from document capture; intercept at window
  // capture first, before it can reset pending checkbox state.
  window.addEventListener('change', event => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.closest('facets-form-component')) return;
    if (input.type !== 'checkbox' && input.type !== 'radio') return;
    const exclusive = input.closest('.facets__inputs-list-item--exclusive');
    if (!exclusive && !input.classList.contains('facets__parent-input') && !input.name.startsWith('filter.')) return;
    if (input.name === LOCKED_FILTER) return;
    // One handler owns the change; Horizon must not submit an intermediate form
    // before all duplicate checkboxes and parent choices have been synchronized.
    event.stopImmediatePropagation();
    cacheAllGroups();
    if (exclusive) chooseExclusive(input, exclusive);
    else if (input.classList.contains('facets__parent-input')) chooseParent(input.dataset.navCollection, input.checked);
    else chooseLeaf(input);
    syncCheckboxes();
    if (isDesktop()) refreshUI();
    else commit(input.closest('facets-form-component'));
  }, true);

  // Opening an accordion never changes a selection.
  document.addEventListener('click', event => {
    const input = event.target.closest?.('.facets__parent-input');
    if (!input) return;
    const details = input.closest('details');
    if (!details) return;
    const open = details.open;
    requestAnimationFrame(() => { details.open = open; });
  }, true);

  window.addEventListener('popstate', () => {
    cacheAllGroups();
    readApplied();
    refreshUI();
  });
  window.addEventListener('scroll', clampPosition, { passive: true });
  window.addEventListener('resize', refreshBar);
  ['filter:update', 'FilterUpdate', 'shopify:section:load'].forEach(name => document.addEventListener(name, scheduleRefresh));

  function init() {
    cacheAllGroups();
    readApplied();
    refreshUI();
    new MutationObserver(records => {
      // Watch both desktop and drawer replacement, ignoring unrelated app DOM.
      const selector = 'facets-form-component, .facets-remove, .facets-block-wrapper';
      if (records.some(record => record.target instanceof Element && (
        record.target.closest(selector) || [...record.addedNodes].some(node =>
          node instanceof Element && (node.matches(selector) || node.querySelector(selector)))
      ))) scheduleRefresh();
    }).observe(document.body, { childList: true, subtree: true });
    customElements.whenDefined('facets-form-component').then(scheduleRefresh);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
